import type { DtoolsItemBucket } from "./dtoolsSiJsonImport";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import type { DtoolsJsonParseResult, DtoolsJsonReconciliation } from "./dtoolsSiJsonImport";

/** Same buckets as import reconciliation (customer-facing line totals). */
export const DTOOLS_STAGING_CUSTOMER_BUCKETS: readonly DtoolsItemBucket[] = [
  "commercial",
  "discount",
  "service",
  "cable",
] as const;

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function isRemovedByVariationOrder(line: DtoolsStagingLine): boolean {
  return line.variationOrderAnnotation?.kind === "removed";
}

export function sumStagingLinesByBuckets(
  lines: DtoolsStagingLine[],
  buckets: readonly DtoolsItemBucket[],
): number {
  const allowed = new Set<string>(buckets);
  return roundMoney(
    lines
      .filter((line) => allowed.has(line.bucket) && !isRemovedByVariationOrder(line))
      .reduce((sum, line) => sum + (Number(line.lineTotal) || 0), 0),
  );
}

export function computeStagingLinesCustomerTotal(lines: DtoolsStagingLine[]): number {
  return sumStagingLinesByBuckets(lines, DTOOLS_STAGING_CUSTOMER_BUCKETS);
}

export function computeStagingLinesOptionalTotal(lines: DtoolsStagingLine[]): number {
  return sumStagingLinesByBuckets(lines, ["optional"]);
}

export function computeStagingLinesNonBillableTotal(lines: DtoolsStagingLine[]): number {
  return sumStagingLinesByBuckets(lines, ["non_billable"]);
}

/** True when a parse item id is absent from staging and not absorbed into mergedSources. */
export function isParseItemAbsentFromStaging(itemId: string, lines: DtoolsStagingLine[]): boolean {
  if (lines.some((line) => line.id === itemId)) return false;
  return !lines.some((line) =>
    (line.mergedSources ?? []).some((source) => source.dtoolsItemId === itemId),
  );
}

/**
 * Detect staff edits/deletions vs the imported parse snapshot.
 * Consolidated accessory ids remain valid when absorbed into mergedSources.
 */
export function isStagingSessionEdited(lines: DtoolsStagingLine[], parseResult: DtoolsJsonParseResult): boolean {
  for (const item of parseResult.items) {
    if (isParseItemAbsentFromStaging(item.id, lines)) return true;
  }
  for (const line of lines) {
    if (!parseResult.items.some((item) => item.id === line.id)) return true;
  }
  const liveTotal = computeStagingLinesCustomerTotal(lines);
  const importTotal = parseResult.reconciliation.linesCustomerTotal;
  if (Math.abs(liveTotal - importTotal) > 0.005) return true;
  return false;
}

export function computeLiveStagingReconciliation(
  lines: DtoolsStagingLine[],
  parseResult: DtoolsJsonParseResult,
): DtoolsJsonReconciliation {
  const importRecon = parseResult.reconciliation;
  const linesCustomerTotal = computeStagingLinesCustomerTotal(lines);
  const linesOptionalTotal = computeStagingLinesOptionalTotal(lines);
  const linesNonBillableTotal = computeStagingLinesNonBillableTotal(lines);

  const headerTotal = parseResult.project.projectTotal;
  const headerSubtotal = parseResult.project.priceWithoutTax;
  const tax = parseResult.project.tax;

  const subtotalCompare = headerSubtotal ?? headerTotal;
  const deltaVsSubtotal =
    subtotalCompare != null ? roundMoney(linesCustomerTotal - subtotalCompare) : null;

  const taxInclusiveExpected =
    headerSubtotal != null && tax != null ? headerSubtotal + tax : headerTotal;
  const deltaVsHeaderTotal =
    taxInclusiveExpected != null
      ? roundMoney(linesCustomerTotal + (tax ?? 0) - taxInclusiveExpected)
      : headerTotal != null
        ? roundMoney(linesCustomerTotal - headerTotal)
        : null;

  const relativeDeltaVsSubtotal =
    subtotalCompare != null && subtotalCompare !== 0
      ? Math.abs(deltaVsSubtotal ?? 0) / Math.abs(subtotalCompare)
      : null;

  const severeMismatch =
    relativeDeltaVsSubtotal != null &&
    relativeDeltaVsSubtotal > 0.02 &&
    Math.abs(deltaVsSubtotal ?? 0) > 50;

  const reconciled = !severeMismatch && (deltaVsHeaderTotal == null || Math.abs(deltaVsHeaderTotal) <= 50);

  return {
    currency: importRecon.currency,
    headerTotal,
    headerSubtotal,
    headerTax: tax,
    linesCustomerTotal,
    linesOptionalTotal,
    linesNonBillableTotal,
    deltaVsHeaderTotal,
    deltaVsSubtotal,
    relativeDeltaVsTotal: relativeDeltaVsSubtotal,
    severeMismatch,
    reconciled,
  };
}

export type DtoolsReviewTotalsBadgeVariant = "default" | "destructive" | "secondary";

export type DtoolsReviewLiveTotalsDisplay = {
  liveCustomerTotal: number;
  originalImportLinesTotal: number;
  originalHeaderTotal: number | null;
  deltaVsOriginalImport: number;
  deltaVsHeader: number | null;
  currency: string;
  stagingEdited: boolean;
  liveReconciledWithHeader: boolean;
  badgeLabel: string;
  badgeVariant: DtoolsReviewTotalsBadgeVariant;
};

export function buildDtoolsReviewLiveTotalsDisplay(
  lines: DtoolsStagingLine[],
  parseResult: DtoolsJsonParseResult,
): DtoolsReviewLiveTotalsDisplay {
  const live = computeLiveStagingReconciliation(lines, parseResult);
  const stagingEdited = isStagingSessionEdited(lines, parseResult);
  const originalImportLinesTotal = parseResult.reconciliation.linesCustomerTotal;
  const deltaVsOriginalImport = roundMoney(live.linesCustomerTotal - originalImportLinesTotal);

  let badgeLabel: string;
  let badgeVariant: DtoolsReviewTotalsBadgeVariant;

  if (stagingEdited) {
    badgeLabel = "Edited — totals changed from original import";
    badgeVariant = "secondary";
  } else if (parseResult.reconciliation.reconciled) {
    badgeLabel = "Totals reconciled";
    badgeVariant = "default";
  } else {
    badgeLabel = "Totals mismatch";
    badgeVariant = "destructive";
  }

  return {
    liveCustomerTotal: live.linesCustomerTotal,
    originalImportLinesTotal,
    originalHeaderTotal: live.headerTotal,
    deltaVsOriginalImport,
    deltaVsHeader: live.deltaVsHeaderTotal,
    currency: live.currency,
    stagingEdited,
    liveReconciledWithHeader: live.reconciled,
    badgeLabel,
    badgeVariant,
  };
}
